import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const PROMPT_HOUR = 20;
const PROMPT_MINUTE = 3;

function todayDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isPastPromptTime() {
  const now = new Date();
  return now.getHours() > PROMPT_HOUR || (now.getHours() === PROMPT_HOUR && now.getMinutes() >= PROMPT_MINUTE);
}

export function useDailyFeedbackPrompt() {
  const { user } = useAuth();
  const [showPopup, setShowPopup] = useState(false);
  const [stateLoaded, setStateLoaded] = useState(false);

  const checkAndShow = useCallback(async () => {
    if (!user?.id) return;
    if (!isPastPromptTime()) return;

    // Account must be at least 7 days old
    const { data: appUser } = await supabase
      .from("app_users")
      .select("created_at")
      .eq("id", user.id)
      .maybeSingle();

    if (!appUser?.created_at) return;

    const daysSinceAccountCreated =
      (new Date().getTime() - new Date(appUser.created_at).getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceAccountCreated < 7) return;

    const today = todayDateStr();

    const { data: state } = await supabase
      .from("user_feedback_prompt_state" as any)
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (state) {
      const s = state as any;
      if (s.disabled_forever || s.has_submitted_feedback) return;
      if (s.last_prompt_seen_date === today || s.last_prompt_dismissed_date === today) return;
    }

    setShowPopup(true);
  }, [user?.id]);

  // On mount + when user changes
  useEffect(() => {
    if (!user?.id) return;
    setStateLoaded(false);
    checkAndShow().finally(() => setStateLoaded(true));
  }, [user?.id, checkAndShow]);

  // On visibility change (app comes back from background)
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === "visible") {
        checkAndShow();
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [checkAndShow]);

  const markSeen = useCallback(async () => {
    if (!user?.id) return;
    const today = todayDateStr();
    await supabase.from("user_feedback_prompt_state" as any).upsert(
      { user_id: user.id, last_prompt_seen_date: today, updated_at: new Date().toISOString() } as any,
      { onConflict: "user_id" }
    );
    setShowPopup(false);
  }, [user?.id]);

  const markDismissed = useCallback(async () => {
    if (!user?.id) return;
    const today = todayDateStr();
    await supabase.from("user_feedback_prompt_state" as any).upsert(
      { user_id: user.id, last_prompt_dismissed_date: today, last_prompt_seen_date: today, updated_at: new Date().toISOString() } as any,
      { onConflict: "user_id" }
    );
    setShowPopup(false);
  }, [user?.id]);

  const markDisabledForever = useCallback(async () => {
    if (!user?.id) return;
    const today = todayDateStr();
    await supabase.from("user_feedback_prompt_state" as any).upsert(
      { user_id: user.id, disabled_forever: true, last_prompt_seen_date: today, updated_at: new Date().toISOString() } as any,
      { onConflict: "user_id" }
    );
    setShowPopup(false);
  }, [user?.id]);

  const markSubmitted = useCallback(async () => {
    if (!user?.id) return;
    const today = todayDateStr();
    await supabase.from("user_feedback_prompt_state" as any).upsert(
      {
        user_id: user.id,
        has_submitted_feedback: true,
        last_feedback_submitted_at: new Date().toISOString(),
        last_prompt_seen_date: today,
        updated_at: new Date().toISOString(),
      } as any,
      { onConflict: "user_id" }
    );
    setShowPopup(false);
  }, [user?.id]);

  return { showPopup, stateLoaded, markSeen, markDismissed, markDisabledForever, markSubmitted };
}
